import {ChangeDetectionStrategy, Component, input, output} from '@angular/core';
import {PortvisitUtils} from '../../../../refdata/portvisit-utils';
import {CommercialReleaseSummary, IE3ConsignmentProcessSummary} from '@portbase/bezoekschip-service-typescriptmodels';
import {AppContext} from '../../../../app-context';
import lodash, {toUpper} from 'lodash';
import {Observable, of} from 'rxjs';
import {terminalOrganisationName, terminals} from '../../../../commercial-release/commercial-release.utils';

/**
 * NEW, standalone-from-the-old-screen form-fields component for the Details tab of the new
 * commercial-release modal. Ported verbatim (queries, commands, conditional branches) from the
 * reverted `CommercialReleaseDetailsComponent`, but reads the release via a signal input instead of
 * an `@Input() summary`, and its template uses `app-form-field` (`master-consignment-details`
 * pattern: `[includeMargin]="false"` inside a `row gy-4`) instead of `app-form-group`, so there is
 * exactly ONE "Release details" section title for the whole modal (this component owns it; the OLD
 * `-declaration` + `-details` pair used to each render their own, which is the "dubbele header" bug
 * this rebuild fixes).
 *
 * Release-to party, Container operator and Inland operator moved OUT to their own "Parties" tab
 * (`CommercialReleaseModalPartiesComponent`), matching the consignments Parties-tab pattern. This
 * component still owns consignment selection (it drives whether Container number/Terminal/CRN render
 * CRN-linked or manual-entry), so it emits the selected consignment upward via
 * `selectedConsignmentChange` for the Parties tab's Container operator lookup to use.
 */
@Component({
  selector: 'app-commercial-release-modal-details',
  templateUrl: './commercial-release-modal-details.component.html',
  styleUrls: ['./commercial-release-modal-details.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class CommercialReleaseModalDetailsComponent {
  refData = PortvisitUtils;
  appContext = AppContext;

  release = input.required<CommercialReleaseSummary>();
  editMode = input<boolean>(false);

  selectedConsignmentChange = output<IE3ConsignmentProcessSummary>();

  selectedConsignment: IE3ConsignmentProcessSummary;

  // Populated at consignment-selection time and bound to the Container number `app-search`'s
  // `[optionsProvider]`. Unlike `app-select`'s `[refreshWatch]`/`[refreshProvider]` pair, `app-search`
  // re-reads its bound `optionsProvider` field lazily on each keystroke, so reassigning this field
  // here is sufficient to "refresh" the option set - no separate refresh-watch mechanism needed.
  containerNumbersOfConsignment: Observable<string[]> = of([]);

  selectConsignment(entity: IE3ConsignmentProcessSummary) {
    const release = this.release();
    delete release.releaseData.equipmentNumber;
    this.selectedConsignment = entity;
    release.releaseData.consignmentNumber = toUpper(entity?.masterConsignment?.consignmentNumber);
    release.releaseData.terminalShortName = entity?.masterConsignment?.dischargeTerminal?.organisationShortName;
    delete release.releaseData.containerOperator;
    release.releaseData.empty = entity?.masterConsignment?.equipments?.every(e => e.empty);
    release.crn = entity?.crn;
    this.containerNumbersOfConsignment = this.containersInConsignment(entity);
    this.selectedConsignmentChange.emit(entity);
  }

  containersInConsignment = (summary: IE3ConsignmentProcessSummary) => {
    return of(summary ? lodash.uniq(lodash.flatMap(
      summary.straight ? summary.masterConsignment?.houseConsignments[0].goodsItems : summary.masterConsignment.goodsItems, g => g.placements)
      .map(p => p.containerIdentificationNumber)) : []);
  }

  protected readonly terminals = terminals;

  protected readonly terminalOrganisationName = terminalOrganisationName;
}
